import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Fab, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';

import { useFabContext } from '../../../contexts/FabContext';
import { useFooterContext, useFooterVisibility } from '../../../contexts/FooterContext';
import { prefersReducedMotion, useScrollGuidance } from '../../../hooks/useScrollGuidance';
import { FabFaceEnum } from '../../../types/shell';

// Desktop nav FAB — replaces the old hover-expanding extended Fab entirely. 56px circular,
// icon-only. Hidden where `fabConfig.desktopFabEnabled === false`.
export const FloatingActionButton = () => {
  const theme = useTheme();
  const { anchorRef } = useFooterContext();
  const { isFooterVisible } = useFooterVisibility();
  const { fabConfig } = useFabContext();

  const { fabState, suppressUntilSettle } = useScrollGuidance({
    anchorRef,
    downEnabled: !!fabConfig.down,
    upEnabled: !!fabConfig.up,
    downAnchorRef: fabConfig.downAnchorRef,
    downThresholds: fabConfig.downThresholds,
    upThresholds: fabConfig.upThresholds,
    downHideAt: fabConfig.downHideAt,
    downIgnoresBar: fabConfig.downIgnoresBar,
  });

  // Freeze the face while hiding: only update the "last shown face" while visible, so the
  // exit fade never shows an icon/label morph.
  const lastFaceRef = useRef<FabFaceEnum.Down | FabFaceEnum.Up>(FabFaceEnum.Down);
  if (fabState !== FabFaceEnum.Hidden) {
    lastFaceRef.current = fabState;
  }
  const face = lastFaceRef.current;

  const faceConfig = face === FabFaceEnum.Up ? fabConfig.up : fabConfig.down;

  const handleClick = useCallback(() => {
    if (!faceConfig) {
      return;
    }
    suppressUntilSettle();
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    faceConfig.onActivate(behavior);
  }, [faceConfig, suppressUntilSettle]);

  const visible = fabState !== FabFaceEnum.Hidden;

  const buttonStyles = useMemo(
    () => ({
      position: 'fixed' as const,
      right: 32,
      // Rides above the sticky bar while it's visible (24px normally, 124px while the bar
      // is shown).
      bottom: isFooterVisible ? 124 : 24,
      zIndex: 30,
      width: 56,
      height: 56,
      minHeight: 56,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.action.active,
      boxShadow: theme.shadows[6],
      '&:hover': {
        backgroundColor: theme.palette.grey[50],
      },
      opacity: visible ? 1 : 0,
      transform: visible ? 'scale(1)' : 'scale(0.6)',
      pointerEvents: visible ? ('auto' as const) : ('none' as const),
      transition: [
        `opacity 250ms cubic-bezier(0.32, 0.72, 0, 1) ${visible ? '150ms' : '0ms'}`,
        `transform 350ms cubic-bezier(0.175, 0.885, 0.32, 1.2) ${visible ? '150ms' : '0ms'}`,
        'bottom 300ms cubic-bezier(0.32, 0.72, 0, 1)',
        'background-color 200ms',
      ].join(', '),
    }),
    [visible, theme, isFooterVisible],
  );

  if (!fabConfig.desktopFabEnabled) {
    return null;
  }

  // Hidden state leaves the a11y tree and tab order; it stays mounted only for the exit fade.
  return (
    <Fab
      onClick={handleClick}
      sx={buttonStyles}
      aria-label={faceConfig?.label}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
    >
      {face === FabFaceEnum.Up ? (
        <ArrowUpwardIcon sx={{ fontSize: 24 }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 24 }} />
      )}
    </Fab>
  );
};
